import type { GameKind } from '../types/game.ts';
import {
  EMPTY_CELL,
  columnOf,
  emptyBoard,
  isBoard,
  isDraw,
  isOver,
  rowOf,
  turnOf,
  winnerOf,
  winningRun,
  withMark,
  type Grid,
  type Mark,
} from './grid.ts';

/**
 * One game's rules, in the shape everything else talks to.
 *
 * **Adding a game is adding one of these and one line in the registry below.**
 * Nothing in the service, the routes, the store or the board component knows
 * which game it is holding — they ask the rules. That is the whole reason this
 * interface exists, and the first version of the schema did not have it: the
 * board column was `CHAR(9)` and the rules were imported directly, so OX was
 * not the first game so much as the only one the system could express.
 */
export interface GameRules {
  readonly kind: GameKind;
  readonly grid: Grid;
  readonly emptyBoard: string;

  isBoard(board: string): boolean;
  turnOf(board: string): Mark;
  winnerOf(board: string): Mark | null;
  winningRun(board: string): number[] | null;
  isDraw(board: string): boolean;
  isOver(board: string): boolean;

  isLegalMove(board: string, cell: number, mark: Mark): boolean;
  applyMove(board: string, cell: number, mark: Mark): string | null;

  /**
   * Where a tap on `cell` actually lands, or null if that tap plays nothing.
   *
   * For OX and Gomoku this is the cell itself when it is empty. For Connect
   * Four it is the lowest empty square in that column, because a piece falls:
   * tapping anywhere in a column means the same move, and an interface that
   * made people aim at the bottom row would be a worse game for no reason.
   *
   * It exists so the board component can stay generic — it asks where a tap
   * goes rather than knowing which games have gravity.
   */
  resolveTap(board: string, cell: number): number | null;
}

/** The parts every grid game answers the same way. */
function gridRules(kind: GameKind, grid: Grid) {
  return {
    kind,
    grid,
    emptyBoard: emptyBoard(grid),
    isBoard: (board: string) => isBoard(board, grid),
    turnOf,
    winnerOf: (board: string) => winnerOf(board, grid),
    winningRun: (board: string) => winningRun(board, grid),
    isDraw: (board: string) => isDraw(board, grid),
    isOver: (board: string) => isOver(board, grid),
  };
}

/** Everything that is legal exactly when the square is free and it is your go. */
function placeAnywhere(kind: GameKind, grid: Grid): GameRules {
  const base = gridRules(kind, grid);
  const isLegalMove = (board: string, cell: number, mark: Mark): boolean => {
    if (!Number.isInteger(cell) || cell < 0 || cell >= grid.width * grid.height) return false;
    if (!base.isBoard(board)) return false;
    if (base.isOver(board)) return false;
    if (board[cell] !== EMPTY_CELL) return false;
    return turnOf(board) === mark;
  };
  return {
    ...base,
    isLegalMove,
    applyMove: (board, cell, mark) =>
      isLegalMove(board, cell, mark) ? withMark(board, cell, mark) : null,
    resolveTap: (board, cell) => {
      if (!base.isBoard(board) || cell < 0 || cell >= board.length) return null;
      return board[cell] === EMPTY_CELL ? cell : null;
    },
  };
}

/* -------------------------------------------------------------------------- */

/** OX. Three in a row on three by three. */
export const TICTACTOE: GameRules = placeAnywhere('tictactoe', { width: 3, height: 3, need: 3 });

/** Gomoku. Five in a row on nine by nine, which fits beside a conversation. */
export const GOMOKU: GameRules = placeAnywhere('gomoku', { width: 9, height: 9, need: 5 });

/**
 * Connect Four. Four in a row on seven by six, and the pieces fall.
 *
 * Gravity is the only thing that makes it different from the two above, and it
 * lives entirely in `resolveTap` and `isLegalMove`: a square is playable only
 * if it is the lowest empty one in its column.
 */
export const CONNECT_FOUR: GameRules = (() => {
  const grid: Grid = { width: 7, height: 6, need: 4 };
  const base = gridRules('connectfour', grid);

  /** The square a piece dropped down this column would come to rest on. */
  const landing = (board: string, column: number): number | null => {
    if (column < 0 || column >= grid.width) return null;
    // From the bottom row upwards: the first empty square is where it stops.
    for (let row = grid.height - 1; row >= 0; row -= 1) {
      const cell = row * grid.width + column;
      if (board[cell] === EMPTY_CELL) return cell;
    }
    // The column is full.
    return null;
  };

  const isLegalMove = (board: string, cell: number, mark: Mark): boolean => {
    if (!Number.isInteger(cell) || cell < 0 || cell >= grid.width * grid.height) return false;
    if (!base.isBoard(board)) return false;
    if (base.isOver(board)) return false;
    if (turnOf(board) !== mark) return false;
    // The square has to be the one a piece would actually land on. Without this
    // a client could place a piece in mid-air, which is not a move this game
    // has — and the server is the only thing standing between it and the board.
    return landing(board, columnOf(cell, grid)) === cell;
  };

  return {
    ...base,
    isLegalMove,
    applyMove: (board, cell, mark) =>
      isLegalMove(board, cell, mark) ? withMark(board, cell, mark) : null,
    resolveTap: (board, cell) => {
      if (!base.isBoard(board) || cell < 0 || cell >= board.length) return null;
      return landing(board, columnOf(cell, grid));
    },
  };
})();

/* -------------------------------------------------------------------------- */

const REGISTRY: Record<GameKind, GameRules> = {
  tictactoe: TICTACTOE,
  connectfour: CONNECT_FOUR,
  gomoku: GOMOKU,
};

/** The rules for a kind. Throws rather than guessing, because a wrong board is worse. */
export function rulesFor(kind: GameKind): GameRules {
  const rules = REGISTRY[kind];
  if (!rules) throw new Error(`No rules for game kind: ${kind}`);
  return rules;
}

/** Exported for the one test that has to prove every kind is wired up. */
export { REGISTRY as GAME_RULES };
export { rowOf, columnOf };
export type { Grid, Mark };
